/**
 * Original, fictional national seals for the eight Sovereign countries, drawn as SVG.
 *
 * Each seal differs in three independent ways so no one needs color to tell them apart:
 * an outline shape, a background pattern and a central charge. Accent colors come from the
 * Okabe-Ito color-blind-safe palette (the same values as the engine's ideology data). None of the
 * seals copies a real flag, coat of arms or party symbol.
 */
import { useId } from 'react';
import type { CountryId } from '@shared/types.ts';

export interface CountryStyle {
  shortName: string;
  accent: string;
  /** Darker accent for text on light surfaces and outlines. */
  deep: string;
  pattern: 'stripes' | 'dots' | 'grid' | 'chevron' | 'waves' | 'cross' | 'diamond' | 'triangles';
}

export const COUNTRY_STYLE: Record<CountryId, CountryStyle> = {
  ironvale: { shortName: 'Ironvale', accent: '#E69F00', deep: '#8a5f00', pattern: 'stripes' },
  brightwater: { shortName: 'Brightwater', accent: '#56B4E9', deep: '#1f6f9c', pattern: 'dots' },
  meridia: { shortName: 'Meridia', accent: '#F0E442', deep: '#8c8412', pattern: 'grid' },
  kestran: { shortName: 'Kestran', accent: '#009E73', deep: '#005e44', pattern: 'chevron' },
  vellmark: { shortName: 'Vellmark', accent: '#CC79A7', deep: '#8a3f68', pattern: 'waves' },
  alvera: { shortName: 'Alvera', accent: '#7FB2F0', deep: '#2e62a3', pattern: 'cross' },
  karsova: { shortName: 'Karsova', accent: '#D55E00', deep: '#8a3c00', pattern: 'diamond' },
  aurem: { shortName: 'Aurem', accent: '#B8A6E8', deep: '#5f4ca3', pattern: 'triangles' },
};

const INK = '#0b1628';

/** Outline shapes on a 64 x 64 grid. */
const SHAPES: Record<CountryId, string> = {
  // Heater shield
  ironvale: 'M8 6h48v24c0 15-11 24-24 29C19 54 8 45 8 30z',
  // Roundel
  brightwater: 'M32 4a28 28 0 1 1 0 56a28 28 0 1 1 0-56z',
  // Lozenge with softened points
  meridia: 'M32 3l27 27a3 3 0 0 1 0 4L32 61 5 34a3 3 0 0 1 0-4z',
  // Hexagon
  kestran: 'M32 4l25 14v28L32 60 7 46V18z',
  // Rounded square
  vellmark: 'M16 5h32a11 11 0 0 1 11 11v32a11 11 0 0 1-11 11H16A11 11 0 0 1 5 48V16A11 11 0 0 1 16 5z',
  // Arched tablet
  alvera: 'M8 28a24 24 0 0 1 48 0v31H8z',
  // Pointed pentagonal shield
  karsova: 'M6 8h52v26L32 60 6 34z',
  // Octagon
  aurem: 'M22 4h20l14 14v28L42 60H22L8 46V18z',
};

/** Central charges, drawn inside a 30 x 30 box centered at (32, 32). */
function Charge({ id, color }: { id: CountryId; color: string }) {
  const s = { fill: 'none', stroke: color, strokeWidth: 2.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (id) {
    case 'ironvale':
      // Three peaks of the vale with a rising beam of light
      return (
        <g {...s}>
          <path d="M19 40l7-10 4 5 5-9 10 14z" fill={color} stroke="none" />
          <path d="M32 21v-3M26.5 23l-1.5-2M37.5 23l1.5-2" />
        </g>
      );
    case 'brightwater':
      // A water drop under a low sun
      return (
        <g {...s}>
          <path d="M32 24c4 5 7 8.5 7 12a7 7 0 0 1-14 0c0-3.5 3-7 7-12z" fill={color} stroke="none" />
          <path d="M22 26a10 10 0 0 1 20 0" />
        </g>
      );
    case 'meridia':
      // A compass rose
      return (
        <g>
          <path d="M32 18l3 11 11 3-11 3-3 11-3-11-11-3 11-3z" fill={color} />
          <circle cx="32" cy="32" r="2.4" fill={INK} />
        </g>
      );
    case 'kestran':
      // A kestrel with spread wings
      return (
        <g {...s}>
          <path d="M18 29c5-1 9 1 14 6 5-5 9-7 14-6-4 2-7 5-9 9l-5 3-5-3c-2-4-5-7-9-9z" fill={color} stroke="none" />
          <path d="M32 26v-3" />
        </g>
      );
    case 'vellmark':
      // A pine over two waves
      return (
        <g {...s}>
          <path d="M32 17l6 9h-3.5l5 7h-15l5-7H26z" fill={color} stroke="none" />
          <path d="M21 38c3 0 3-2 5.5-2s2.5 2 5.5 2 3-2 5.5-2 2.5 2 5.5 2M21 43c3 0 3-2 5.5-2s2.5 2 5.5 2 3-2 5.5-2 2.5 2 5.5 2" />
        </g>
      );
    case 'alvera':
      // A three-arch bridge
      return (
        <g {...s}>
          <path d="M18 27h28" />
          <path d="M19 42V31M45 42V31" />
          <path d="M19 42a4.5 5 0 0 1 9 0M27.5 42a4.5 5 0 0 1 9 0M36 42a4.5 5 0 0 1 9 0" />
          <path d="M18 31h28" />
        </g>
      );
    case 'karsova':
      // A gate tower
      return (
        <g>
          <path d="M22 44V24h4v3h3v-3h6v3h3v-3h4v20z" fill={color} />
          <path d="M29 44v-7a3 3 0 0 1 6 0v7z" fill={INK} />
        </g>
      );
    case 'aurem':
      // A sun disc over dunes
      return (
        <g {...s}>
          <circle cx="32" cy="27" r="6" fill={color} stroke="none" />
          <path d="M18 39c5-4 9-4 14 0 5-4 9-4 14 0M21 44c4-3 7-3 11 0 4-3 7-3 11 0" />
        </g>
      );
  }
}

function PatternDef({ id, kind, color }: { id: string; kind: CountryStyle['pattern']; color: string }) {
  const stroke = { stroke: color, strokeWidth: 1.6, fill: 'none' };
  switch (kind) {
    case 'stripes':
      return (
        <pattern id={id} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path d="M0 0v7" {...stroke} strokeWidth={2.2} />
        </pattern>
      );
    case 'dots':
      return (
        <pattern id={id} width="7" height="7" patternUnits="userSpaceOnUse">
          <circle cx="3.5" cy="3.5" r="1.4" fill={color} />
        </pattern>
      );
    case 'grid':
      return (
        <pattern id={id} width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M8 0H0v8" {...stroke} />
        </pattern>
      );
    case 'chevron':
      return (
        <pattern id={id} width="10" height="7" patternUnits="userSpaceOnUse">
          <path d="M0 6l5-4 5 4" {...stroke} />
        </pattern>
      );
    case 'waves':
      return (
        <pattern id={id} width="12" height="7" patternUnits="userSpaceOnUse">
          <path d="M0 4c3 0 3-2.5 6-2.5S9 4 12 4" {...stroke} />
        </pattern>
      );
    case 'cross':
      return (
        <pattern id={id} width="9" height="9" patternUnits="userSpaceOnUse">
          <path d="M4.5 2.5v4M2.5 4.5h4" {...stroke} />
        </pattern>
      );
    case 'diamond':
      return (
        <pattern id={id} width="9" height="9" patternUnits="userSpaceOnUse">
          <path d="M4.5 1.5l3 3-3 3-3-3z" {...stroke} />
        </pattern>
      );
    case 'triangles':
      return (
        <pattern id={id} width="9" height="8" patternUnits="userSpaceOnUse">
          <path d="M4.5 1.5l3.5 5.5H1z" fill={color} />
        </pattern>
      );
  }
}

export interface CountryEmblemProps {
  country: CountryId;
  size?: number;
  /** Accessible name; defaults to "<Name> seal". Pass "" to hide from assistive technology. */
  label?: string;
  className?: string;
  /** Grey out (for example an AI nation in a list, or a lost nation). */
  muted?: boolean;
}

export function CountryEmblem({ country, size = 40, label, className, muted }: CountryEmblemProps) {
  const uid = useId().replace(/:/g, '');
  const st = COUNTRY_STYLE[country];
  const shape = SHAPES[country];
  const name = label ?? `${st.shortName} seal`;
  const clip = `em-c-${uid}`;
  const pat = `em-p-${uid}`;
  return (
    <svg
      className={className ? `emblem ${className}` : 'emblem'}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role={name ? 'img' : undefined}
      aria-label={name || undefined}
      aria-hidden={name ? undefined : true}
      focusable="false"
      style={muted ? { filter: 'grayscale(0.85)', opacity: 0.7 } : undefined}
    >
      <defs>
        <clipPath id={clip}>
          <path d={shape} />
        </clipPath>
        <PatternDef id={pat} kind={st.pattern} color={INK} />
      </defs>
      <path d={shape} fill={st.accent} />
      <g clipPath={`url(#${clip})`} opacity={0.3}>
        <rect width="64" height="64" fill={`url(#${pat})`} />
      </g>
      <circle cx="32" cy="32" r="16.5" fill={INK} />
      <circle cx="32" cy="32" r="16.5" fill="none" stroke={st.accent} strokeWidth="1.2" opacity="0.6" />
      <Charge id={country} color={st.accent} />
      <path d={shape} fill="none" stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />
    </svg>
  );
}

/** Small colored chip: seal plus short name, for lists and chat. */
export function CountryChip({ country, size = 22, name }: { country: CountryId; size?: number; name?: string }) {
  return (
    <span className="country-chip">
      <CountryEmblem country={country} size={size} label="" />
      <span>{name ?? COUNTRY_STYLE[country].shortName}</span>
    </span>
  );
}
